/**
 * workspaceIntegrityGuard.js — REGRA DE OURO V1.0
 * =================================================
 * Auto-verificação bidirecional Master ↔ Portal para cadernos de autos.
 *
 * Detecta:
 *  - Documentos órfãos (cadernoId aponta para workspace inexistente)
 *  - GED links de workspaces estrangeiros
 *  - Workspaces duplicados (mesmo pieceType + clientId sem alias diferenciador)
 *  - Workspaces sem campos críticos (clientId, pieceType)
 *  - Inconsistências de visibilidade (workspace existe no Master mas não no Portal)
 *
 * Integrado em:
 *  - PortalVault.jsx  (lado cliente — roda ao carregar cadernos)
 *  - AutosDashboard.jsx (lado master — roda ao listar cadernos)
 */

const PREFIX = '[INTEGRITY]';

/**
 * Verifica integridade no lado PORTAL (cliente).
 * Roda quando workspaceRows, docs e gedLinks são carregados.
 *
 * @param {Object} params
 * @param {Array}  params.workspaces   - workspaceRows do onSnapshot (clientId filter)
 * @param {Array}  params.docs         - documentos do onSnapshot (clientId filter)
 * @param {Array}  params.gedLinks     - GED links do onSnapshot (partiesRefs filter)
 * @param {string} params.clientId     - ID do cliente logado
 * @returns {{ ok: boolean, issues: string[], orphanDocIds: string[], foreignGedIds: string[] }}
 */
export function checkPortalIntegrity({ workspaces = [], docs = [], gedLinks = [], clientId = '' }) {
  const issues = [];
  const orphanDocIds = [];
  const foreignGedIds = [];

  const wsIds = new Set((workspaces || []).map((w) => String(w?.id || '').trim()).filter(Boolean));

  // 1. Documentos com cadernoId apontando para workspace inexistente
  for (const d of (docs || [])) {
    const cid = String(d?.cadernoId || '').trim();
    if (cid && cid !== '__AVULSO__' && !wsIds.has(cid)) {
      orphanDocIds.push(String(d?.id || ''));
      issues.push(`Doc "${String(d?.title || d?.id || '').substring(0, 40)}" aponta para workspace inexistente (cadernoId: ${cid})`);
    }
  }

  // 2. GED links de workspaces que não pertencem ao cliente
  for (const g of (gedLinks || [])) {
    const wsId = String(g?.workspaceId || '').trim();
    if (wsId && !wsIds.has(wsId)) {
      foreignGedIds.push(String(g?.id || ''));
      issues.push(`GED link "${String(g?.fileName || g?.id || '').substring(0, 40)}" pertence a workspace estrangeiro (${wsId})`);
    }
  }

  // 3. Workspaces sem clientId (dados corrompidos)
  for (const w of (workspaces || [])) {
    const wClientId = String(w?.clientId || '').trim();
    if (!wClientId) {
      issues.push(`Workspace ${w?.id} sem clientId — dado corrompido`);
    } else if (wClientId !== clientId) {
      issues.push(`Workspace ${w?.id} pertence a outro cliente (${wClientId} !== ${clientId}) — cross-contamination`);
    }
  }

  const ok = issues.length === 0;

  if (!ok) {
    console.warn(`${PREFIX} PORTAL — ${issues.length} problema(s) detectado(s) para cliente ${clientId}:`);
    issues.forEach((i) => console.warn(`  ⚠️ ${i}`));
  } else {
    console.info(`${PREFIX} PORTAL OK — ${workspaces.length} workspace(s), ${docs.length} doc(s), ${gedLinks.length} GED link(s) — sem inconsistências.`);
  }

  return { ok, issues, orphanDocIds, foreignGedIds };
}

/**
 * Verifica integridade no lado MASTER (admin).
 * Roda quando workspaces de um cliente são carregados.
 *
 * @param {Object} params
 * @param {Array}  params.workspaces - todos os workspaces visíveis para o cliente selecionado
 * @param {string} params.clientId   - ID do cliente selecionado
 * @returns {{ ok: boolean, issues: string[], duplicateGroups: Array }}
 */
export function checkMasterIntegrity({ workspaces = [], clientId = '' }) {
  const issues = [];
  const duplicateGroups = [];

  // 0. Workspaces com clientId vazio (dado corrompido) — checado ANTES do
  // filtro por clientId de propósito (achado real, caso [PET_A] 22/08/2026):
  // um workspace sem clientId nunca bate `=== clientId` pra NENHUM cliente,
  // então ficava invisível pra este guard independente de quem estivesse
  // selecionado — "MASTER OK" mesmo com um caderno corrompido pendurado.
  // clientName é só pra dar contexto no aviso, não é o campo corrigido.
  for (const w of (workspaces || [])) {
    if (!String(w?.clientId || '').trim()) {
      issues.push(
        `Workspace ${w?.id} (clientName: "${w?.clientName || '?'}", alias: "${w?.alias || '?'}") ` +
        `está com clientId vazio — corrompe o contexto do CRM (cai pra fallback errado em outras telas)`
      );
    }
  }

  const clientWs = (workspaces || []).filter((w) => String(w?.clientId || '').trim() === clientId);

  // 1. Workspaces sem campos críticos
  for (const w of clientWs) {
    if (!String(w?.pieceType || '').trim()) {
      issues.push(`Workspace ${w?.id} sem pieceType — campo obrigatório ausente`);
    }
    if (!String(w?.clientName || '').trim()) {
      issues.push(`Workspace ${w?.id} sem clientName — campo obrigatório ausente`);
    }
  }

  // 2. Detecção de duplicatas (mesmo pieceType sem alias diferenciador)
  const byType = new Map();
  for (const w of clientWs) {
    const pt = String(w?.pieceType || '').trim().toLowerCase();
    if (!pt) continue;
    if (!byType.has(pt)) byType.set(pt, []);
    byType.get(pt).push(w);
  }

  for (const [type, group] of byType) {
    if (group.length <= 1) continue;
    // Verificar se todos têm alias diferenciador
    const aliases = group.map((w) => String(w?.alias || '').trim().toLowerCase());
    const uniqueAliases = new Set(aliases.filter(Boolean));
    // Se aliases vazios ou repetidos → duplicata suspeita
    if (uniqueAliases.size < group.length) {
      duplicateGroups.push({ type, workspaces: group });
      issues.push(
        `${group.length} workspace(s) do tipo "${type}" sem alias diferenciador — possível duplicata ` +
        `(IDs: ${group.map((w) => w?.id).join(', ')})`
      );
    }
  }

  const ok = issues.length === 0;

  if (!ok) {
    console.warn(`${PREFIX} MASTER — ${issues.length} problema(s) para cliente ${clientId}:`);
    issues.forEach((i) => console.warn(`  ⚠️ ${i}`));
  } else {
    console.info(`${PREFIX} MASTER OK — ${clientWs.length} workspace(s) para cliente ${clientId} — sem inconsistências.`);
  }

  return { ok, issues, duplicateGroups };
}

/**
 * Verificação cruzada: compara o que o Master vê vs o que o Portal deveria ver.
 * Útil para testes automatizados.
 *
 * @param {Object} params
 * @param {Array}  params.masterWorkspaces  - workspaces do onSnapshot sem filtro
 * @param {Array}  params.portalWorkspaces  - workspaces do onSnapshot com clientId filter
 * @param {string} params.clientId
 * @returns {{ ok: boolean, issues: string[], missingInPortal: string[], extraInPortal: string[] }}
 */
export function crossCheckMasterPortal({ masterWorkspaces = [], portalWorkspaces = [], clientId = '' }) {
  const issues = [];
  const missingInPortal = [];
  const extraInPortal = [];

  // Workspaces que o Master mostra para este cliente
  const masterForClient = (masterWorkspaces || []).filter((w) => String(w?.clientId || '').trim() === clientId);
  const masterIds = new Set(masterForClient.map((w) => String(w?.id || '').trim()));
  const portalIds = new Set((portalWorkspaces || []).map((w) => String(w?.id || '').trim()));

  // O que existe no Master mas não aparece no Portal
  for (const id of masterIds) {
    if (!portalIds.has(id)) {
      missingInPortal.push(id);
      issues.push(`Workspace ${id} existe no Master mas NÃO aparece no Portal — filtro de visibilidade falhou`);
    }
  }

  // O que aparece no Portal mas não existe no Master (fantasma)
  for (const id of portalIds) {
    if (!masterIds.has(id)) {
      extraInPortal.push(id);
      issues.push(`Workspace ${id} aparece no Portal mas NÃO existe no Master — caderno fantasma`);
    }
  }

  const ok = issues.length === 0;

  if (!ok) {
    console.warn(`${PREFIX} CROSS-CHECK — ${issues.length} divergência(s):`);
    issues.forEach((i) => console.warn(`  ⚠️ ${i}`));
  }

  return { ok, issues, missingInPortal, extraInPortal };
}
